import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiX, FiStar } from 'react-icons/fi';
import { supabase } from '../config/supabase';
import { useOrderItemRating } from '../hooks/useProductRatings';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 500px;
  padding: 32px;
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const CloseButton = styled.button`
  padding: 8px;
  border: none;
  background: none;
  cursor: pointer;
  color: #6b7280;
  font-size: 24px;
  transition: all 0.2s;

  &:hover {
    color: #1f2937;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const StarsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const StarButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: ${props => props.filled ? '#fbbf24' : '#d1d5db'};
  font-size: 32px;
  transition: all 0.2s;
  line-height: 1;

  &:hover {
    color: ${props => props.filled ? '#fbbf24' : '#fcd34d'};
    transform: scale(1.1);
  }
`;

const Textarea = styled.textarea`
  padding: 12px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
`;

const CancelButton = styled(Button)`
  background: white;
  color: #6b7280;
  border: 1px solid #e5e7eb;

  &:hover {
    background: #f9fafb;
  }
`;

const SubmitButton = styled(Button)`
  background: #5efba9;
  color: #1f2937;

  &:hover {
    background: #4de89e;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ProductInfo = styled.div`
  padding: 16px;
  background: #f9fafb;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const ProductName = styled.div`
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
`;

const ProductDetails = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

export default function RatingModal({ orderItem, order, existingRating, onClose, onSubmitted }) {
  const [stars, setStars] = useState(existingRating?.stars || 0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [body, setBody] = useState(existingRating?.body || '');
  const [submitting, setSubmitting] = useState(false);

  const { rating: loadedRating, loading: ratingLoading } = useOrderItemRating(orderItem?.id);

  // Update form when existing rating loads
  useEffect(() => {
    if (loadedRating && !existingRating) {
      setStars(loadedRating.stars || 0);
      setBody(loadedRating.body || '');
    }
  }, [loadedRating, existingRating]);

  const handleStarClick = (starValue) => {
    setStars(starValue);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (stars === 0) {
      alert('Please select a rating (1-5 stars)');
      return;
    }

    if (!orderItem || !order) {
      alert('Missing order information');
      return;
    }

    setSubmitting(true);

    try {
      const ratingData = {
        product_id: orderItem.product_id,
        variant_id: orderItem.variant_id || null,
        order_id: order.id,
        order_item_id: orderItem.id,
        stars: stars,
        title: null, // Optional title field
        body: body.trim() || null,
        status: 'approved', // Set status (or 'pending' if moderation needed)
        purchase_verified: true, // Mark as verified since it's from a purchase
        created_by: order.profile_id
      };

      let error;
      if (existingRating || loadedRating) {
        // Update existing rating
        const ratingId = existingRating?.id || loadedRating?.id;
        const { error: updateError } = await supabase
          .from('product_ratings')
          .update({
            stars: ratingData.stars,
            title: ratingData.title,
            body: ratingData.body,
            status: 'approved', // Ensure status is approved when updating
            updated_at: new Date().toISOString()
          })
          .eq('id', ratingId);
        error = updateError;
      } else {
        // Create new rating
        const { error: insertError } = await supabase
          .from('product_ratings')
          .insert([ratingData]);
        error = insertError;
      }

      if (error) throw error;

      alert(existingRating || loadedRating ? 'Review updated successfully!' : 'Thank you for your review!');
      onSubmitted();
    } catch (err) {
      console.error('Error submitting rating:', err);
      alert(`Failed to submit rating: ${err.message || 'Please try again.'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const productName = orderItem?.product_name || 'Product';
  const variantInfo = orderItem?.variant_id ? `Variant: ${orderItem.sku || orderItem.variant_id}` : '';
  const quantity = orderItem?.quantity || 1;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{existingRating || loadedRating ? 'Edit Your Review' : 'Rate This Product'}</ModalTitle>
          <CloseButton onClick={onClose}>
            <FiX />
          </CloseButton>
        </ModalHeader>

        <ProductInfo>
          <ProductName>{productName}</ProductName>
          <ProductDetails>
            Order #{order?.id?.substring(0, 8)}... • {quantity} {quantity === 1 ? 'item' : 'items'}
            {variantInfo && ` • ${variantInfo}`}
          </ProductDetails>
        </ProductInfo>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Your Rating *</Label>
            <StarsContainer>
              {[1, 2, 3, 4, 5].map((star) => (
                <StarButton
                  key={star}
                  type="button"
                  filled={star <= (hoveredStar || stars)}
                  onMouseEnter={() => setHoveredStar(star)}
                  onMouseLeave={() => setHoveredStar(0)}
                  onClick={() => handleStarClick(star)}
                >
                  <FiStar style={{ fill: star <= (hoveredStar || stars) ? 'currentColor' : 'none' }} />
                </StarButton>
              ))}
              <span style={{ marginLeft: '12px', color: '#6b7280', fontSize: '14px' }}>
                {stars > 0 && `${stars} ${stars === 1 ? 'star' : 'stars'}`}
              </span>
            </StarsContainer>
          </FormGroup>

          <FormGroup>
            <Label>Your Review (Optional)</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Share your experience with this product..."
              maxLength={1000}
            />
            <div style={{ fontSize: '12px', color: '#9ca3af', textAlign: 'right', marginTop: '4px' }}>
              {body.length}/1000 characters
            </div>
          </FormGroup>

          <ButtonGroup>
            <CancelButton type="button" onClick={onClose} disabled={submitting}>
              Cancel
            </CancelButton>
            <SubmitButton type="submit" disabled={submitting || stars === 0}>
              {submitting ? 'Submitting...' : existingRating || loadedRating ? 'Update Review' : 'Submit Review'}
            </SubmitButton>
          </ButtonGroup>
        </Form>
      </ModalContainer>
    </ModalOverlay>
  );
}

